// frontend/src/components/common/ui/FilterPanel.tsx
// Enhanced filter panel with collapsible design and better UX

import React, { useState } from 'react';
import { 
  Filter, 
  ChevronDown, 
  ChevronUp, 
  X, 
  Search,
  RotateCcw 
} from 'lucide-react';
import PageFilters from './PageFilters';
import type { FilterField } from './PageFilters';

export interface FilterPanelProps {
  fields: FilterField[];
  values: Record<string, any>;
  onChange: (key: string, value: any) => void;
  onClear: () => void;
  hasActiveFilters: boolean;
  variant?: 'default' | 'compact' | 'sidebar';
  defaultExpanded?: boolean;
  showToggle?: boolean;
  title?: string;
  className?: string;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  fields,
  values,
  onChange,
  onClear,
  hasActiveFilters,
  variant = 'default',
  defaultExpanded = false,
  showToggle = true,
  title = 'Filters',
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded || hasActiveFilters);

  const activeFilterCount = Object.values(values).filter(v => v && v !== 'all' && v !== '').length;

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleQuickClear = () => {
    onClear();
    // Keep panel open after clearing so user can see the change
  };

  if (variant === 'compact') {
    return (
      <div className={`bg-white border border-gray-200 rounded-lg ${className}`}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Quick Filters</span>
              {activeFilterCount > 0 && (
                <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </div>
            {hasActiveFilters && (
              <button
                onClick={handleQuickClear}
                className="text-xs text-gray-500 hover:text-red-600 flex items-center gap-1"
              >
                <RotateCcw className="h-3 w-3" />
                Clear
              </button>
            )}
          </div>
          <PageFilters
            fields={fields}
            values={values}
            onChange={onChange}
            onClear={onClear}
            hasActiveFilters={hasActiveFilters}
            variant="compact"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white border border-gray-200 rounded-lg shadow-sm ${className}`}>
      {showToggle && (
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={handleToggle}
        >
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">{title}</span>
            {activeFilterCount > 0 && (
              <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full min-w-[20px] text-center">
                {activeFilterCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuickClear();
                }}
                className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50"
              >
                <X className="h-3 w-3" />
                Clear
              </button>
            )}
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </div>
        </div>
      )}

      {(isExpanded || !showToggle) && (
        <div className="border-t border-gray-200 p-4">
          <PageFilters
            fields={fields}
            values={values}
            onChange={onChange}
            onClear={onClear}
            hasActiveFilters={hasActiveFilters}
            variant={variant}
          />
          
          {hasActiveFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} applied
                </span>
                <button
                  onClick={handleQuickClear}
                  className="text-sm text-red-600 hover:text-red-800 font-medium flex items-center gap-1"
                >
                  <RotateCcw className="h-3 w-3" />
                  Clear All Filters
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Enhanced pagination with hide/show toggle
export interface SmartPaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  className?: string;
  autoHide?: boolean;
  minItemsToShow?: number;
}

export const SmartPagination: React.FC<SmartPaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  className = '',
  autoHide = true,
  minItemsToShow = 10
}) => {
  // Auto-hide pagination if there are very few items
  if (autoHide && totalItems <= minItemsToShow && totalPages <= 1) {
    return null;
  }

  // Show compact version for small datasets
  if (totalPages <= 3) {
    return (
      <div className={`flex items-center justify-center gap-2 py-4 ${className}`}>
        <span className="text-sm text-gray-600">
          {totalItems} item{totalItems !== 1 ? 's' : ''}
        </span>
        {totalPages > 1 && (
          <>
            <span className="text-gray-300">•</span>
            <div className="flex gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`px-3 py-1 text-sm rounded transition-colors ${
                    page === currentPage
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // Full pagination for larger datasets
  return (
    <div className={`bg-white border-t border-gray-200 px-4 py-4 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
          <span className="font-medium">
            {Math.min(currentPage * itemsPerPage, totalItems)}
          </span>{' '}
          of <span className="font-medium">{totalItems}</span> results
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <div className="flex gap-1">
            {/* Show first page */}
            {currentPage > 3 && (
              <>
                <button
                  onClick={() => onPageChange(1)}
                  className="px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded"
                >
                  1
                </button>
                {currentPage > 4 && <span className="px-2 text-gray-400">...</span>}
              </>
            )}

            {/* Show current page and neighbors */}
            {Array.from(
              { length: Math.min(5, totalPages) },
              (_, i) => Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i
            )
              .filter(page => page <= totalPages)
              .map(page => (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`px-3 py-2 text-sm rounded transition-colors ${
                    page === currentPage
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              ))}

            {/* Show last page */}
            {currentPage < totalPages - 2 && (
              <>
                {currentPage < totalPages - 3 && <span className="px-2 text-gray-400">...</span>}
                <button
                  onClick={() => onPageChange(totalPages)}
                  className="px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded"
                >
                  {totalPages}
                </button>
              </>
            )}
          </div>

          <button
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;